import React from 'react';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  Award, 
  BookOpen, 
  ShieldCheck, 
  Briefcase, 
  GraduationCap, 
  Building2, 
  Compass, 
  CheckCircle2 
} from 'lucide-react';
import SanjayDhir from '../../Media/SanjayDhir.png';

const areasOfInterest = [
  "Strategic Innovation",
  "Ambidexterity",
  "Strategic Thinking",
  "Strategic Management",
  "Strategic Alliances & Joint Ventures",
  "Technology Transfer",
  "Creative Problem Solving",
  "Management of Change & Transformation"
];

const consultingPartners = [
  { name: "Bill & Melinda Gates Foundation", context: "National COVID-19 Vaccine Rollout" },
  { name: "Ministry of Skill Development & Entrepreneurship", context: "SANKALP Project Evaluation" },
  { name: "Ministry of Education (HRD)", context: "SPARC International Research Initiative" },
  { name: "Ministry of Minority Affairs", context: "Haj Policy, Scholarship Schemes & Waqf Impact" },
  { name: "ICSSR / UNIL Switzerland / NRCT Thailand", context: "International Visiting Scholar Research" },
  { name: "Government of India", context: "Unnat Bharat Abhiyan Core Committee" }
];

export default function AboutMe() {
  return (
    <div className="container py-5 min-vh-100 position-relative text-light" style={{ zIndex: 1 }}>
      
      {/* 1. HERO PROFILE OVERVIEW */}
      <div className="row align-items-center g-5 mb-5">
        
        {/* Left Bio Column */}
        <div className="col-12 col-lg-7" data-aos="fade-right">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3 sub-badge">
            <Sparkles size={15} className="text-cyan" />
            <span className="small text-light fw-medium">Department of Management Studies • IIT Delhi</span>
          </div>

          <h1 className="display-4 fw-bold text-white mb-2">
            Prof. <span className="text-gradient">Sanjay Dhir</span>
          </h1>
          <p className="lead fs-5 text-secondary mb-4">
            Fellow (Ph.D.), IIM Lucknow • Professor & Area Chair of Strategic Management
          </p>

          <p className="text-light opacity-90 lh-lg mb-4">
            Prof. Sanjay Dhir is a Professor and Area Chair of Strategic Management at the Department of Management Studies, IIT Delhi. Prior to entering academia, he spent over three years in the Research & Development Department at <strong>Mahindra and Mahindra (Automotive) Ltd.</strong>, grounding his theoretical frameworks in enterprise-scale execution.
          </p>

          {/* Quick Distinction Metrics */}
          <div className="row g-3">
            <div className="col-6 col-sm-4">
              <div className="p-3 rounded-3 glass-card-sm text-center h-100">
                <GraduationCap size={20} className="text-warning mb-1" />
                <div className="fw-bold fs-5 text-white">2019–20</div>
                <div className="text-secondary small" style={{ fontSize: '0.72rem' }}>IITD Teaching Excellence</div>
              </div>
            </div>
            <div className="col-6 col-sm-4">
              <div className="p-3 rounded-3 glass-card-sm text-center h-100">
                <Award size={20} className="text-cyan mb-1" />
                <div className="fw-bold fs-5 text-white">2020 & 2021</div>
                <div className="text-secondary small" style={{ fontSize: '0.72rem' }}>InsideIIM Prof of the Year</div>
              </div>
            </div>
            <div className="col-12 col-sm-4">
              <div className="p-3 rounded-3 glass-card-sm text-center h-100">
                <BookOpen size={20} className="text-info mb-1" />
                <div className="fw-bold fs-5 text-white">60+ Scopus</div>
                <div className="text-secondary small" style={{ fontSize: '0.72rem' }}>Top Tier Publications</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Portrait Column */}
        <div className="col-12 col-lg-5 text-center" data-aos="fade-left">
          <div className="position-relative p-2 rounded-4 glass-panel border-glow d-inline-block w-100">
            <img 
              src={SanjayDhir} 
              alt="Prof. Sanjay Dhir" 
              className="img-fluid rounded-4 shadow-lg w-100"
              style={{ maxHeight: '800px', objectFit: 'cover' }}
            />
          </div>
        </div>

      </div>

      {/* 2. PUBLIC POLICY & HIGH COURT ADVISORY */}
      <div className="p-4 p-md-5 rounded-4 glass-panel mb-5" data-aos="fade-up">
        <div className="d-flex align-items-center gap-2 mb-3">
          <ShieldCheck size={22} className="text-cyan" />
          <h4 className="fw-bold text-white mb-0">High Court & State Policy Governance</h4>
        </div>
        <p className="text-secondary lh-lg mb-3">
          Nominated as a strategic expert by the Delhi Government for the <strong>GNCTD State-Level Committee on Oxygen Infrastructure</strong> during the peak of the COVID-19 pandemic. 
        </p>
        <p className="text-secondary lh-lg mb-0">
          Representing IIT Delhi, he formulated and submitted the high-level research report titled <em>"IITD Review and Recommendations for Management of Oxygen during Covid-19 Crisis for GNCTD"</em> directly to the <strong>Hon’ble High Court of Delhi</strong> to resolve supply-chain bottlenecks and clinical distribution.
        </p>
      </div>

      {/* 3. SCHOLARSHIP & CASE WRITING */}
      <div className="row g-4 mb-5">
        <div className="col-lg-6" data-aos="fade-up">
          <div className="p-4 rounded-4 glass-panel h-100">
            <div className="d-flex align-items-center gap-2 mb-3">
              <BookOpen size={20} className="text-info" />
              <h5 className="fw-bold text-white mb-0">Leading Journal Scholarship</h5>
            </div>
            <p className="text-secondary small lh-lg mb-3">
              Over 60 publications cataloged across premier ABDC and Scopus journals including:
            </p>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {[
                "International Business Review",
                "Journal of Business Research",
                "Technological Forecasting & Social Change",
                "Thunderbird International Business Review"
              ].map((journal, i) => (
                <span key={i} className="badge bg-dark border border-secondary border-opacity-50 text-light px-2 py-1 small">
                  {journal}
                </span>
              ))}
            </div>
            <p className="text-secondary small mb-0">
              Regular contributor to global conference proceedings: <em>AoM, AIB, SMS, SMA, and BAM</em>.
            </p>
          </div>
        </div>

        <div className="col-lg-6" data-aos="fade-up" data-aos-delay="100">
          <div className="p-4 rounded-4 glass-panel h-100">
            <div className="d-flex align-items-center gap-2 mb-3">
              <Briefcase size={20} className="text-warning" />
              <h5 className="fw-bold text-white mb-0">Harvard & Ivey Case Publishing</h5>
            </div>
            <p className="text-secondary small lh-lg mb-3">
              Bridging academic management constructs with actual organizational crises, his case studies are published with the <strong>Richard Ivey School of Business</strong> (Western University, Canada) and distributed internationally through <strong>Harvard Business Publishing</strong>.
            </p>
            <p className="text-secondary small mb-0">
              These pedagogical instruments are used in global executive and MBA classrooms to evaluate turnaround strategies and capability renewal in volatile environments.
            </p>
          </div>
        </div>
      </div>

      {/* 4. SPONSORED CONSULTING & STRATEGIC PROJECTS */}
      <div className="p-4 p-md-5 rounded-4 glass-panel mb-5" data-aos="fade-up">
        <h4 className="fw-bold text-white mb-3">National & Global Advisory Engagements</h4>
        <p className="text-secondary small mb-4">
          He has spearheaded high-level strategic consulting and policy-impact assessments across central ministries, state missions, and international foundations:
        </p>
        
        <div className="row g-3">
          {consultingPartners.map((item, idx) => (
            <div key={idx} className="col-md-6 col-lg-4">
              <div className="p-3 rounded-3 glass-card-sm h-100 border-start border-3 border-info">
                <h6 className="fw-bold text-white mb-1" style={{ fontSize: '0.9rem' }}>{item.name}</h6>
                <p className="text-secondary small mb-0">{item.context}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 5. RESEARCH INTERESTS */}
      <div className="p-4 rounded-4 glass-panel" data-aos="fade-up">
        <h5 className="fw-bold text-white mb-3 d-flex align-items-center gap-2">
          <Sparkles size={18} className="text-warning" /> Areas of Specialization & Research Focus
        </h5>
        <div className="d-flex flex-wrap gap-2">
          {areasOfInterest.map((interest, idx) => (
            <span 
              key={idx} 
              className="badge px-3 py-2 rounded-pill fw-medium"
              style={{
                background: 'rgba(0, 242, 254, 0.08)',
                border: '1px solid rgba(0, 242, 254, 0.25)',
                color: '#e2e8f0',
                fontSize: '0.85rem'
              }}
            >
              {interest}
            </span>
          ))}
        </div>
      </div>

    </div>
  );
}